import { ApolloCache } from '@provision/graphql-client';
import { MESSAGE_REACTIONS_FRAGMENT } from './conversation-message-reaction.operations';
import {
  ConversationMessageReaction,
  MessageReactionsData,
} from './conversation-message-reaction.types';

type ReactionKey = Pick<
  ConversationMessageReaction,
  'conversationMessageId' | 'userId' | 'emoji'
>;

// One reaction per user per emoji, so (userId, emoji) identifies it even while its id is still
// the optimistic placeholder.
const isSameReaction = (a: ReactionKey, b: ReactionKey) =>
  a.userId === b.userId && a.emoji === b.emoji;

function updateMessageReactions(
  cache: ApolloCache<unknown>,
  conversationMessageId: string,
  update: (reactions: ConversationMessageReaction[]) => ConversationMessageReaction[],
): void {
  cache.updateFragment<MessageReactionsData>(
    {
      id: cache.identify({ __typename: 'ConversationMessage', id: conversationMessageId }),
      fragment: MESSAGE_REACTIONS_FRAGMENT,
      fragmentName: 'MessageReactions',
    },
    (data) => data && { ...data, reactions: update(data.reactions ?? []) },
  );
}

/**
 * Puts a reaction on its cached message, replacing any entry for the same user and emoji.
 * A message that isn't cached with its reactions is left alone.
 */
export function upsertReactionInCache(
  cache: ApolloCache<unknown>,
  reaction: ConversationMessageReaction,
): void {
  updateMessageReactions(cache, reaction.conversationMessageId, (reactions) => [
    ...reactions.filter((existing) => !isSameReaction(existing, reaction)),
    { ...reaction, __typename: 'ConversationMessageReaction' },
  ]);
}

/** Takes a user's emoji reaction off its cached message. */
export function removeReactionFromCache(
  cache: ApolloCache<unknown>,
  reaction: ReactionKey,
): void {
  updateMessageReactions(cache, reaction.conversationMessageId, (reactions) =>
    reactions.filter((existing) => !isSameReaction(existing, reaction)),
  );
}
